import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions
} from '@floating-ui/react';
import { useEffect, useRef, useState } from 'react';

import './periodNavigator.scss';

type AriaLabels = {
  first: string;
  last: string;
  next: string;
  previous: string;
};

const DEFAULT_ARIA_LABELS: AriaLabels = {
  first: 'First period',
  last: 'Last period',
  next: 'Next period',
  previous: 'Previous period'
};

type Props = {
  ariaLabels?: AriaLabels;
  disabled?: boolean;
  onChange: (newIndex: number) => void;
  periodIndex: number;
  periodLabels: string[];
  totalPeriods: number;
};

export default function PeriodNavigator({
  ariaLabels = DEFAULT_ARIA_LABELS,
  disabled = false,
  onChange,
  periodIndex,
  periodLabels,
  totalPeriods
}: Readonly<Props>) {
  const [isOpen, setIsOpen] = useState(false);
  const activeItemRef = useRef<HTMLButtonElement>(null);

  const { context, refs, x, y } = useFloating({
    middleware: [offset(6), flip(), shift({ padding: 8 })],
    onOpenChange: setIsOpen,
    open: isOpen,
    placement: 'bottom',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate
  });

  const { getFloatingProps } = useInteractions([useDismiss(context)]);

  useEffect(() => {
    if (isOpen) {
      activeItemRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [isOpen]);

  const isFirst = periodIndex === 0;
  const isLast = periodIndex === totalPeriods - 1;
  const currentLabel = periodLabels[periodIndex] ?? '';

  return (
    <div className={`periodNavigator${disabled ? ' periodNavigator--disabled' : ''}`}>
      <div className="periodNavigator__nav" ref={refs.setReference}>
        <button
          aria-label={ariaLabels.first}
          className="periodNavigator__skipButton"
          disabled={disabled || isFirst}
          onClick={() => onChange(0)}
          type="button"
        >
          <i aria-hidden="true" className="fa-solid fa-angles-left" />
        </button>

        <button
          aria-label={ariaLabels.previous}
          className="periodNavigator__navButton"
          disabled={disabled || isFirst}
          onClick={() => onChange(periodIndex - 1)}
          type="button"
        >
          <i aria-hidden="true" className="periodNavigator__navArrow fa-solid fa-chevron-left" />
        </button>

        <button
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          className="periodNavigator__label"
          disabled={disabled}
          onClick={() => setIsOpen(o => !o)}
          type="button"
        >
          <span>{currentLabel}</span>
          <i aria-hidden="true" className="periodNavigator__calendarIcon fa-regular fa-calendar" />
        </button>

        <button
          aria-label={ariaLabels.next}
          className="periodNavigator__navButton"
          disabled={disabled || isLast}
          onClick={() => onChange(periodIndex + 1)}
          type="button"
        >
          <i aria-hidden="true" className="periodNavigator__navArrow fa-solid fa-chevron-right" />
        </button>

        <button
          aria-label={ariaLabels.last}
          className="periodNavigator__skipButton"
          disabled={disabled || isLast}
          onClick={() => onChange(totalPeriods - 1)}
          type="button"
        >
          <i aria-hidden="true" className="fa-solid fa-angles-right" />
        </button>
      </div>

      {isOpen && !disabled && (
        <FloatingPortal>
          <div
            {...getFloatingProps()}
            className="periodNavigator__dropdown"
            ref={refs.setFloating}
            role="listbox"
            style={{ left: x ?? 0, position: 'fixed', top: y ?? 0 }}
          >
            {periodLabels.map((label, i) => (
              <button
                aria-selected={i === periodIndex}
                className={`periodNavigator__dropdownItem${i === periodIndex ? ' periodNavigator__dropdownItem--active' : ''}`}
                key={label}
                onClick={() => {
                  onChange(i);
                  setIsOpen(false);
                }}
                ref={i === periodIndex ? activeItemRef : undefined}
                role="option"
                type="button"
              >
                {label}
              </button>
            ))}
          </div>
        </FloatingPortal>
      )}
    </div>
  );
}
